/**
 * Утилиты скачивания файлов из ответов API
 */

/**
 * Извлекает имя файла из заголовка Content-Disposition.
 * Сначала берётся filename* (RFC 5987, UTF-8 — так приходит кириллица), затем filename.
 * @param contentDisposition - Значение заголовка Content-Disposition
 * @param fallback - Имя, если заголовка нет или имя не разобрать
 */
export const getFileNameFromContentDisposition = (contentDisposition: string | null | undefined, fallback: string): string => {
    if (!contentDisposition) {
        return fallback;
    }

    const extendedMatch = /filename\*\s*=\s*([^;]+)/i.exec(contentDisposition);
    if (extendedMatch) {
        const value = extendedMatch[1].trim().replace(/^"(.*)"$/, '$1');
        const encoded = value.replace(/^[\w-]+'[^']*'/, '');
        try {
            const decoded = decodeURIComponent(encoded);
            if (decoded) {
                return decoded;
            }
        } catch {
            // Битое кодирование — пробуем обычный filename.
        }
    }

    const plainMatch = /(?:^|;)\s*filename\s*=\s*("([^"]*)"|[^;]*)/i.exec(contentDisposition);
    const plain = (plainMatch?.[2] ?? plainMatch?.[1] ?? '').trim();
    if (plain && !plain.startsWith('=?')) {
        try {
            return decodeURIComponent(plain);
        } catch {
            return plain;
        }
    }

    return fallback;
};

/**
 * Сохраняет содержимое ответа как файл через временную ссылку.
 * @param data - Содержимое файла
 * @param fileName - Имя сохраняемого файла
 * @param contentType - MIME-тип, если data ещё не Blob
 */
export const saveBlobAsFile = (data: BlobPart, fileName: string, contentType?: string): void => {
    const blob = data instanceof Blob ? data : new Blob([data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Достаёт message из JSON-ошибки, пришедшей в ответе с responseType: 'blob'.
 * @param error - Ошибка axios
 */
export const readBlobErrorMessage = async (error: unknown): Promise<string | undefined> => {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    if (!(data instanceof Blob)) {
        return undefined;
    }

    try {
        const parsed = JSON.parse(await data.text());
        return typeof parsed?.message === 'string' ? parsed.message : undefined;
    } catch {
        return undefined;
    }
};